import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { IconButton } from '@mui/material'
import { QueryObserverResult } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCurrentAccountLink, useInstallationForm, useWallboxesIdentityStatus } from 'src/Libs/flow/installation/hooks'
import { FlagIcon } from 'src/Libs/flow/icons'
import { useUser } from 'src/Hooks/useUser'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { updateWallboxIdentityStatus } from '../../api'
import { WallboxIdentityStatusDto } from '../../types'
import { usePortalMutation } from 'src/Libs/api'

const roundUpToThousands = (num: number): number => Math.ceil(num / 1000) * 1000

type IdentifyWallboxProps = {
  installationId: number
  modbusTcpIndex: number
  isWittyLink?: boolean
}

/** Flag-Button to update macAddress / identity */
export const ModbusTcpIdentifyButton: React.FC<IdentifyWallboxProps> = ({ installationId, modbusTcpIndex, isWittyLink = false }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const { userData } = useUser()

  const accountLink = useCurrentAccountLink(installationId)

  const { control } = useInstallationForm()

  const [emc, modbusTcp] = useWatch({
    control,
    name: ['emcDevice', `chargingStations.modbusTcp.${modbusTcpIndex}`],
  })

  const emcHardwareDeviceId = emc?.emcHardwareDeviceId ?? null
  const wallboxMacAddress = modbusTcp.pairingId

  const hasWriteAccess = accountLink ? accountLink.permissionType === 'rw' : false
  const isSuperUser = userData?.level === 'EXT_SERVICE_PLUS'

  const isActivePairedWallbox = modbusTcp.deviceStatus !== 'unknown' && modbusTcp.isPaired

  const { data: wallboxesIdentityStatus, refetch } = useWallboxesIdentityStatus(emcHardwareDeviceId ?? null, isActivePairedWallbox)

  const { mutateAsync } = usePortalMutation({
    mutationFn: (props: { emcHardwareDeviceId: string; wallboxMacAddress: string }) =>
      updateWallboxIdentityStatus(props.emcHardwareDeviceId, props.wallboxMacAddress, authFetch),
    onSuccess() {
      enqueueSuccessStack(t('installation.identificationConfirmation.success'))
    },
    onError() {
      enqueueErrorStack(t('installation.identificationConfirmation.error'))
    },
  })

  useEffect(() => {
    if (!wallboxesIdentityStatus || wallboxesIdentityStatus.lockedAt === null || wallboxesIdentityStatus.timeout === null) return

    const now = Date.now()
    const timeout = roundUpToThousands(wallboxesIdentityStatus.timeout - (now - wallboxesIdentityStatus.lockedAt))
    const timeoutId = setTimeout(() => {
      refetch()
    }, timeout)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [wallboxesIdentityStatus, refetch])

  const hasPermissionsToEdit = hasWriteAccess || isSuperUser

  if (!emcHardwareDeviceId || !wallboxMacAddress || !hasPermissionsToEdit || isWittyLink)
    return (
      <IconButton disabled size="large">
        <FlagIcon />
      </IconButton>
    )

  const isDisabled = !!wallboxesIdentityStatus || !isActivePairedWallbox

  const handleClick = (): Promise<QueryObserverResult<Nullable<WallboxIdentityStatusDto>, Error>> =>
    mutateAsync({ emcHardwareDeviceId, wallboxMacAddress }).then(() => refetch())

  return (
    <IconButton onClick={handleClick} disabled={isDisabled} size="large" color={'warning'}>
      <FlagIcon />
    </IconButton>
  )
}
